import trackingApiClient from './trackingApiClient';
import type {
  UserActivityApiItem,
  UserActivityApiResponse,
  UserActivityItem,
  UserListItem,
  TrackingApiResponse,
  TrackingResponse,
  TrackingSession,
  TrackingSegment,
  GeoPoint,
  RawTrackingSession,
  TrackingTaskLocation,
} from '../types/tracking';
import { formatDeviceLabel } from '../utils/mapHelpers';

/** Map a raw API item to the normalised shape used by the UI */
function mapActivityItem(raw: UserActivityApiItem): UserActivityItem {
  const activity = raw.activity;
  const checkinTimes = activity?.checkin_times ?? [];

  // A user is live if any checkin has no checkout
  const isLive = checkinTimes.some(ct => ct.checkin_time != null && ct.checkout_time == null);

  // Earliest checkin_time
  const allCheckins = checkinTimes
    .map(ct => ct.checkin_time)
    .filter((t): t is string => t != null);
  const checkInTime = allCheckins.length > 0
    ? allCheckins.reduce((a, b) => (a < b ? a : b))
    : null;

  // Latest checkout_time
  const allCheckouts = checkinTimes
    .map(ct => ct.checkout_time)
    .filter((t): t is string => t != null);
  const checkOutTime = allCheckouts.length > 0
    ? allCheckouts.reduce((a, b) => (a > b ? a : b))
    : null;

  // Use last_location (falls back to first_location)
  const location = activity?.last_location ?? activity?.first_location;

  // Latest phone the user checked in with. `device_details` is parallel
  // to `checkin_times` so the last non-null entry represents the most
  // recent check-in. We walk from the end so a final-null doesn't shadow
  // an earlier real entry.
  const deviceDetails = activity?.device_details ?? [];
  let latestDevice: { manufacturer: string | null; model: string | null } | null = null;
  for (let i = deviceDetails.length - 1; i >= 0; i--) {
    const d = deviceDetails[i];
    if (d && (d.manufacturer || d.model)) {
      latestDevice = { manufacturer: d.manufacturer, model: d.model };
      break;
    }
  }
  const deviceModel = latestDevice
    ? formatDeviceLabel(latestDevice.manufacturer, latestDevice.model)
    : null;

  return {
    user_id: raw.id,
    name: raw.name,
    employee_id: raw.employee_id ?? null,
    device_model: deviceModel,
    role: raw.role ?? [],
    program_id: raw.program_id ?? [],
    email: raw.email ?? null,
    phone_number: raw.phone_number ?? null,
    permanent_address: raw.permanent_address ?? null,
    district: raw.district ?? null,
    status: raw.status ?? null,
    check_in_time: checkInTime,
    check_out_time: checkOutTime,
    total_distance_km: 0,
    last_latitude: location?.latitude ?? null,
    last_longitude: location?.longitude ?? null,
    last_location_time: checkOutTime ?? checkInTime,
    is_live: isLive,
  };
}

/**
 * Interpolate timestamps across coordinates within a segment.
 * Distributes time evenly between start_time and end_time.
 */
function interpolateTimestamps(start: string, end: string, count: number): string[] {
  if (count === 0) return [];
  if (count === 1) return [start];
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  return Array.from({ length: count }, (_, i) => {
    const t = startMs + ((endMs - startMs) * i) / (count - 1);
    return new Date(t).toISOString();
  });
}

/** Transform a raw tracking session into the normalised TrackingSession used by the UI */
function mapRawSession(raw: RawTrackingSession): TrackingSession {
  // Tag each point with its parent segment index so the playback hook can
  // skip cross-segment haversine bridges (those bridges added ~20 km of
  // phantom distance to multi-segment days; the server already excluded
  // them from `total_distance_meters`).
  const segments: TrackingSegment[] = raw.movement_segments.map((seg, segIdx) => {
    const timestamps = interpolateTimestamps(seg.start_time, seg.end_time, seg.coordinates.length);
    const points: GeoPoint[] = seg.coordinates.map(([lng, lat], i) => ({
      longitude: lng,
      latitude: lat,
      recorded_at: timestamps[i],
      segment_index: segIdx,
    }));
    // Backend uses `osrm_coordinates` on GET /tracking (empty array `[]`
    // means "no path"). The match-route response uses `osrm_path`. Accept
    // either so the mapper works regardless of which endpoint the payload
    // came from — a raw `[]` normalises to `null` so the UI's "no
    // correction" branch is a simple `!segment.osrm_path` check.
    const rawOsrmPath = seg.osrm_coordinates ?? seg.osrm_path ?? null;
    const osrmPath =
      Array.isArray(rawOsrmPath) && rawOsrmPath.length > 0
        ? (rawOsrmPath as [number, number][])
        : null;
    return {
      id: typeof seg.id === 'number' ? seg.id : null,
      index: segIdx,
      start_time: seg.start_time,
      end_time: seg.end_time,
      points,
      total_distance_meters: seg.total_distance_meters ?? 0,
      osrm_path: osrmPath,
      osrm_total_distance: seg.osrm_total_distance ?? null,
      is_osrm_applied: seg.is_osrm_applied ?? null,
      updated_by: seg.updated_by ?? null,
    };
  });

  const geoPoints: GeoPoint[] = segments.flatMap((s) => s.points);

  const totalDistanceMeters = raw.movement_segments.reduce(
    (sum, s) => sum + (s.total_distance_meters ?? 0),
    0,
  );

  // Map tasks_by_location to TrackingTaskLocation[]
  const tasks: TrackingTaskLocation[] = (raw.tasks_by_location ?? [])
    .filter(tbl => tbl.items.length > 0)
    .map((tbl, locIdx) => ({
      latitude: tbl.location.latitude,
      longitude: tbl.location.longitude,
      recorded_at: raw.checkout_time ?? geoPoints[geoPoints.length - 1]?.recorded_at ?? raw.checkin_time,
      tasks: tbl.items.map((t, i) => ({
        id: `${raw.checkin_id}-loc${locIdx}-task-${i}`,
        title: t.title,
        is_active: false,
        description: t.description || null,
      })),
      remarks: tbl.remarks.length > 0 ? tbl.remarks.join('; ') : null,
    }));

  return {
    checkin_time: raw.checkin_time,
    checkout_time: raw.checkout_time,
    geo_points: geoPoints,
    segments,
    total_distance_meters: totalDistanceMeters,
    tasks,
  };
}

export const trackingService = {
  /**
   * Fetch user activity for a given date and optional program filter.
   * Fetches all pages and aggregates into a single list.
   * Supports cancellation via AbortSignal; partial page failures are tolerated.
   * GET /backoffice/user-activity?date=YYYY-MM-DD&program_id=TBTP,WM
   */
  async getUserActivity(
    date: string,
    programs?: string[],
    status?: string,
    signal?: AbortSignal
  ): Promise<UserActivityItem[]> {
    const pageSize = 100;
    const baseParams: Record<string, string> = { date, page_size: String(pageSize) };
    if (programs && programs.length > 0) baseParams.program_id = programs.join(',');
    if (status) baseParams.status = status;

    const firstPage = await trackingApiClient.get<UserActivityApiResponse>('/user-activity', {
      params: { ...baseParams, page: '1' },
      signal,
    });

    const total = firstPage.data.total ?? firstPage.data.data.length;
    const totalPages = Math.ceil(total / pageSize);

    const allItems: UserActivityApiItem[] = [...firstPage.data.data];

    if (totalPages > 1) {
      const remainingRequests = [];
      for (let page = 2; page <= totalPages; page++) {
        remainingRequests.push(
          trackingApiClient.get<UserActivityApiResponse>('/user-activity', {
            params: { ...baseParams, page: String(page) },
            signal,
          })
        );
      }
      const results = await Promise.allSettled(remainingRequests);
      for (const result of results) {
        if (result.status === 'fulfilled') {
          allItems.push(...result.value.data.data);
        } else {
          // Re-throw on cancellation so the caller can bail out cleanly
          const err = result.reason;
          if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || signal?.aborted) {
            throw err;
          }
          console.warn('[trackingService] Failed to fetch a user-activity page; continuing with partial data.', err);
        }
      }
    }

    return allItems.map(mapActivityItem);
  },

  /**
   * Fetch user list with optional program filter.
   * GET /user-list?program=TBTP
   */
  async getUserList(program?: string): Promise<UserListItem[]> {
    const params: Record<string, string> = {};
    if (program) params.program = program;

    const { data } = await trackingApiClient.get<UserListItem[]>('/user-list', { params });
    return data;
  },

  /**
   * Fetch detailed tracking data (sessions, geo_points, tasks) for a user on a date.
   * GET /tracking?user_id=UUID&date=YYYY-MM-DD
   * Actual response: RawTrackingUser[] (top-level array)
   */
  async getUserTracking(userId: string, date: string): Promise<TrackingResponse> {
    const { data } = await trackingApiClient.get<TrackingApiResponse>('/tracking', {
      params: { user_id: userId, date },
    });

    // API returns an array of users; find the one matching this userId
    const userEntry = data.find(u => u.user_id === userId);
    const sessions: TrackingSession[] = (userEntry?.sessions ?? []).map(mapRawSession);

    return { sessions };
  },

  /**
   * Compute an OSRM-corrected path for a segment.
   *
   * Spec: docs/location-tracking/osrm-optimization.md §Endpoints.1.
   *
   *   - `segment_id` only         → correct gaps *inside* that segment.
   *   - `segment_id` + `extend`   → bridge the gap to the next segment in
   *                                 the same check-in and store the result
   *                                 on the passed (earlier) segment.
   *
   * Returns the newly-computed path/distance. The server responds `200`
   * with the existing values when a path has already been computed —
   * clients can treat that as a no-op.
   *
   * Auth: LOCATION.TRACKING_EDITOR or ADMIN. Others get `403`.
   */
  async matchRoute(
    segmentId: number,
    options: { extend?: boolean } = {},
  ): Promise<MatchRouteResponse> {
    const params: Record<string, string> = { segment_id: String(segmentId) };
    if (options.extend) params.extend = 'true';
    const { data } = await trackingApiClient.get<MatchRouteResponse>('/match-route', { params });
    return data;
  },

  /**
   * Apply or reject a previously-computed OSRM path for a segment. The
   * server treats a re-issue of the current action as a `200` no-op so
   * this is safe to retry.
   *
   * Spec: docs/location-tracking/osrm-optimization.md §Endpoints.2.
   */
  async setOsrmPathAction(
    segmentId: number,
    action: 'apply' | 'reject',
  ): Promise<{ message: string }> {
    const { data } = await trackingApiClient.post<{ message: string }>(
      '/insert-osrm-path',
      { segment_id: segmentId, action },
    );
    return data;
  },

  /**
   * Recompute the daily `total_modified_distance_meters` for a user.
   * Server requires `date` to be at least one day before today (i.e.
   * only fully-summarised days can be synced).
   *
   * Spec: docs/location-tracking/osrm-optimization.md §Endpoints.3.
   */
  async syncOsrmData(userId: string, date: string): Promise<SyncOsrmDataResponse> {
    const { data } = await trackingApiClient.post<SyncOsrmDataResponse>(
      '/sync-osrm-data',
      { user_id: userId, date },
    );
    return data;
  },
};

// ── OSRM-endpoint response shapes ───────────────────────────────────────────

export interface MatchRouteResponse {
  message: string;
  segment_id: number;
  /** `[longitude, latitude]` pairs. */
  osrm_path: [number, number][];
  /** Meters. */
  osrm_total_distance: number;
}

export interface SyncOsrmDataResponse {
  updated_distance_meters: number;
  updated_by: string;
  updated_at: string;
}
